// src/pages/Counselling/Requests/Components/RequestStatusMenu.tsx
/**
 * Changes a request's status once it has been sorted out by other means, for example putting a
 * duplicate back to Pending or marking one Cancelled.
 *
 * ⚠️ **Approved and Rejected are not ordinary statuses.** `changeStatus` returns **400** for them,
 * because reaching either has side effects a bare status write would skip: approval creates the care
 * case and its members, rejection notifies the requester. Writing the status directly would leave a
 * request marked approved with no case behind it.
 *
 * They are still **listed**, rather than quietly filtered out, because a missing option cannot explain
 * itself and "why can't I set Approved?" is exactly the question to answer in place. Selecting one
 * opens the proper Approve or Reject flow instead of calling `changeStatus`, so the menu is a complete
 * map of the state space and every entry does the right thing.
 */
import { Box, Button, Loader, Menu, Text } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import type { StatusLookup } from '@/pages/Overview/Hooks/useStatusCounts';
import { REQUEST_STATUS, isWorkflowOwned } from '@/pages/Counselling/requestStatusIds';

export interface RequestStatusMenuProps {
  statuses: StatusLookup[];
  currentStatusID: number;
  busy?: boolean;
  disabled?: boolean;
  /** Ordinary status change, straight through `changeStatus`. */
  onChangeStatus: (statusID: number) => void;
  /** Selected Approved: open the approve flow instead. */
  onApprove: () => void;
  /** Selected Rejected: open the reject flow instead. */
  onReject: () => void;
}

export default function RequestStatusMenu({
  statuses,
  currentStatusID,
  busy = false,
  disabled = false,
  onChangeStatus,
  onApprove,
  onReject,
}: RequestStatusMenuProps) {
  const handleSelect = (statusID: number) => {
    if (statusID === REQUEST_STATUS.Approved) { onApprove(); return; }
    if (statusID === REQUEST_STATUS.Rejected) { onReject(); return; }
    onChangeStatus(statusID);
  };

  return (
    <Menu shadow="md" width={280} position="bottom-end">
      <Menu.Target>
        <Button
          variant="default"
          rightSection={busy ? <Loader size={14} /> : <IconChevronDown size={14} />}
          disabled={disabled || busy || statuses.length === 0}
        >
          Change status
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {statuses.map((s) => {
          const isCurrent = s.statusID === currentStatusID;
          const workflow = isWorkflowOwned(s.statusID);
          return (
            <Menu.Item
              key={s.statusID}
              disabled={isCurrent}
              onClick={() => handleSelect(s.statusID)}
              leftSection={
                <Box
                  w={10}
                  h={10}
                  style={{ borderRadius: '50%', backgroundColor: s.color || 'var(--mantine-color-gray-5)' }}
                />
              }
              rightSection={workflow
                ? <Text size="xs" c="dimmed">opens flow</Text>
                : undefined}
            >
              {s.name ?? `Status ${s.statusID}`}
            </Menu.Item>
          );
        })}
        <Menu.Divider />
        <Menu.Label>
          Approving or rejecting opens the full step, so the case is created and the requester is told.
        </Menu.Label>
      </Menu.Dropdown>
    </Menu>
  );
}
